import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { uniq, sortBy, uniqBy } from 'lodash';
import * as moment from 'moment';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AlertService, PlayerDashboardService, QuestionServiceService } from '../../../../services';

@Component({
  selector: 'app-all-questions',
  templateUrl: './all-questions.component.html',
  styleUrls: ['./all-questions.component.css']
})
export class AllQuestionsComponent implements OnChanges {
  @Input() userID;
  @Input() clubProgramID;
  @Input() status: number;
  @Input() cancelText = 'Cancel';

  @Output() cancel = new EventEmitter();
  @Output() save = new EventEmitter();

  questionForm: FormGroup;

  fees = [];
  questions = [];

  checkboxPrefix = 'checkBox-';
  selectedCheckBoxPrefix = 'SelectedItems-';
  noAvailableFees: boolean;

  public actMath: string;
  public actVerbal: string;
  public actWritten: string;
  public actEnglish: string;
  public actList = [];
  public satReading: string;
  public satMath: string;
  public satList = [];
  sub: Subscription;
  hasHeight: boolean;
  isECNLRegistered: boolean;

  constructor(
    public router: Router,
    private fb: FormBuilder,
    private rs: QuestionServiceService,
    private loader: Ng4LoadingSpinnerService,
    private alert: AlertService,
    private playerService: PlayerDashboardService,
  ) {
  }

  ngOnChanges() {
    this.getQuestions();
  }

  chkName(qlabel: any) {
    return this.checkboxPrefix + qlabel.replace(/\s/g, '');
  }

  chkNameControl(qlabel: any) {
    const ctrl = this.checkboxPrefix + qlabel.replace(/\s/g, '');
    const checkBoxName = this.questionForm.controls[ctrl] as FormArray;
    return checkBoxName;
  }

  isInvalidField(field: string) {
    return this.questionForm.controls[field] && this.questionForm.controls[field].touched && this.questionForm.controls[field].invalid;
  }

  submit() {
    this.loader.show();
    const objs = {
      clubProgramID: Number(this.clubProgramID),
      userID: Number(this.userID),
      answers: this.getAnswers(),
      clubUserProgramFeeID: 0
    };

    this.rs.addQuestionAnswer(objs).subscribe(
      () => {
        this.loader.hide();
        this.alert.success('Answers updated');
        this.save.emit();
      },
      () => {
        this.loader.hide();
        this.alert.error('Something went wrong');
      }
    )
  }

  private getQuestions() {
    this.loader.show();
    const groups: any = {};

    const getAct = this.rs.getUserActScores(this.userID).subscribe(rs => {
      //this.tgsloaderService.setLoader(false);
      this.actMath = rs.acTmath;
      this.actVerbal = rs.acTverbal;
      this.actEnglish = rs.acTenglish;
      this.actWritten = rs.acTwritten;
      this.satMath = rs.saTmath;
      this.satReading = rs.saTreading;
      getAct.unsubscribe();
    });

    this.playerService.isPlayerRegisteredWithOrg(this.userID).subscribe(({ data }) => {
      this.isECNLRegistered = !!data;
      this.rs.getQuestionList(this.clubProgramID, this.userID)
        .subscribe(res => {
          this.questions = res;
          this.hasHeight = !!this.questions.find(q => q.label === "Player Height Inch");
          this.questions.forEach(que => {
            if (this.isECNLRegistered && (que.label === 'Player First Name' || que.label === 'Player Last Name' || 'Player Middle Name')) {
              groups[que.label] = [{ value: que.answer || '', disabled: true }, que.isRequired ? [Validators.required] : []];
            } else if (que.questionTypeID === 1) {
              const opts = que.queOptionList;
              if (opts === null) { return; }
  
              const checkboxGroup = new FormArray(opts.map(item => new FormGroup({
                clubProgramQuestionOptionID: new FormControl(item.clubProgramQuestionOptionID),
                optionDescription: new FormControl(item.optionDescription),
                checkbox: new FormControl(item.isSelected)
              })));
  
              let hiddenControl;
              if (que.isRequired === 1) {
                hiddenControl = new FormControl(this.mapItems(checkboxGroup.value), Validators.required);
              } else {
                hiddenControl = new FormControl(this.mapItems(checkboxGroup.value));
              }
  
              checkboxGroup.valueChanges.pipe(take(1)).subscribe((v) => {
                hiddenControl.setValue(this.mapItems(v));
              });
  
              const qlabel = que.label.replace(/\s/g, '');
              groups[this.checkboxPrefix + qlabel] = checkboxGroup;
              groups[this.selectedCheckBoxPrefix + que.clubProgramQuestionID] = hiddenControl;
            } else {
              if (que.label.toLowerCase().includes('gpa')) {
                let gpaint = '';
                let gpadec = '';
                if (que.answer) {
                  const answer = (+que.answer).toFixed(2);
                  gpaint = answer.split('.')[0];
                  gpadec = answer.split('.')[1];
                }
                groups['GPAint'] = [gpaint, que.isRequired ? [Validators.required] : []];
                groups['GPAdec'] = [gpadec, que.isRequired ? [Validators.required] : []];
              } else if (que.label.toLowerCase() === 'act score') {
                  groups['actWritten'] = [que.answer || '', que.isRequired ? [Validators.required] : []];
                  groups['actVerbal'] = [que.answer || '', que.isRequired ? [Validators.required] : []];
                  groups['actMath'] = [que.answer || '', que.isRequired ? [Validators.required] : []];
                  groups['actEnglish'] = [que.answer || '', que.isRequired ? [Validators.required] : []];
              } else if (que.label.toLowerCase().includes('sat score')) {
                  groups['satReading'] = [que.answer || '', que.isRequired ? [Validators.required] : []];
                  groups['satMath'] = [que.answer || '', que.isRequired ? [Validators.required] : []];
              } else {
                groups[que.label] = [que.answer || '', que.isRequired ? [Validators.required] : []];
              }
            }
          });
          this.questionForm = this.fb.group(groups);
          this.loader.hide();
  
          if (this.sub) {
            this.sub.unsubscribe();
          } else {
            this.sub = new Subscription();
          }
  
          if (this.questionForm.controls['Committed Status']) {
            const committedStatusControl = this.questionForm.controls['Committed Status'];
  
            const committedSchool = this.questions.find(q => q.label === 'Committed School');
            const committedDate = this.questions.find(q => q.label === 'Committed Date');
            const committedSchoolState = this.questions.find(q => q.label === 'Committed School State');
  
            committedSchool._queOptionList = committedSchool.queOptionList.slice();
            committedSchool.queOptionList = [];
  
            committedSchool.hide = true;
            committedDate.hide = true;
            committedSchoolState.hide = true;
  
            const committedSchoolControl = this.questionForm.controls['Committed School'];
            const committedDateControl = this.questionForm.controls['Committed Date'];
            const committedSchoolStateControl = this.questionForm.controls['Committed School State'];
  
            this.sub.add(committedStatusControl.valueChanges.subscribe(s => {
              switch (+s) {
                case 0:
                case -1:
                  // committedStatusControl.setErrors({ required: true });
                  // break;
                  committedSchool.isRequired = 0;
                  committedSchoolControl.setValidators(null);
                  committedSchoolControl.updateValueAndValidity();
  
                  committedDate.isRequired = 0;
                  committedDateControl.setValidators(null);
                  committedDateControl.updateValueAndValidity();
  
                  committedSchoolState.isRequired = 0;
                  committedSchoolStateControl.setValidators(null);
                  committedSchoolStateControl.updateValueAndValidity();
  
                  committedSchool.hide = true;
                  committedSchoolState.hide = true;
                  break;
                case 1:
                case 2:
                  committedSchool.isRequired = 1;
                  committedSchool.hide = false;
                  committedSchoolControl.setValidators([Validators.required]);
                  committedSchoolControl.updateValueAndValidity();
  
                  committedDate.isRequired = 1;
                  committedDateControl.setValidators([Validators.required]);
                  committedDateControl.setValue(moment().format('MM/DD/YYYY'));
                  committedDate.hide = false;
                  committedDateControl.updateValueAndValidity();
  
                  committedSchoolState.isRequired = 1;
                  committedSchoolState.hide = false;
                  committedSchoolStateControl.setValidators([Validators.required]);
                  committedSchoolStateControl.updateValueAndValidity();
                  break;
              }
              this.questionForm.updateValueAndValidity();
            }));
  
            this.sub.add(committedSchoolStateControl.valueChanges.subscribe(stateID => {
              committedSchool.queOptionList = committedSchool._queOptionList.filter(school => +school.stateID === +stateID);
            }));
          }
  
          if (this.questionForm.controls['High School State']) {
            const sc = this.questionForm.controls['High School State'];
            this.sub.add(sc.valueChanges.subscribe(s => {
              this.rs.getListOfCities(+s).subscribe(cities => {
                const city = this.questions.find(q => q.label === 'High School City');
                if (city) {
                  city.queOptionList = uniq(cities).sort().map(c => ({
                    clubProgramQuestionOptionID: c,
                    isSelected: false,
                    optionDescription: c
                  }));
                }
              });
            }));
  
            if (this.questionForm.controls['High School City']) {
              this.sub.add(this.questionForm.controls['High School City'].valueChanges.subscribe(s => {
                this.rs.getListOfSchools(sc.value, s).subscribe(schools => {
                  const hs = this.questions.find(q => q.label === 'High School');
                  if (hs) {
                    hs.queOptionList = sortBy(uniqBy<any>(schools, 'highschoolID'), 'name').map(school => ({
                      clubProgramQuestionOptionID: school.highschoolID,
                      isSelected: false,
                      optionDescription: school.name
                    }));
                  }
                });
              }));
            }
          }
        });
    });

  }

  private mapItems(items) {
    const selectedItems = items
      .filter((item) => item.checkbox).map(item => ({
        clubProgramAnswerID: item.clubProgramQuestionOptionID,
        description: item.optionDescription,
      }));
    return selectedItems.length ? selectedItems : null;
  }

  private getAnswers() {
    const answers = [];
    for (let i = 0; i < this.questions.length; i++) {
      const qlist = this.questions[i];
      let qOptionList = [];
      if (this.questions[i].questionTypeID === 1) {
        const selectedOptionValue = this.questionForm.controls[this.selectedCheckBoxPrefix + qlist.clubProgramQuestionID].value;
        if (selectedOptionValue !== null) {
          qOptionList = selectedOptionValue;
        }
      } else {
      }

      let answ: any;
      if (this.questions[i].questionTypeID === 1) {
        answ = '';

        const data = {
          clubProgramAnswerID: Number(qlist.clubProgramAnswerID),
          clubProgramQuestionID: Number(qlist.clubProgramQuestionID),
          userID: Number(this.userID),
          answer: answ,
          questionLabel: qlist.label,
          answerOptionList: qOptionList.length !== 0 ? qOptionList : []
        };
        answers.push(data);
      } else {
        if (qlist.label.toLowerCase().includes('gpa')) {
          const txtGpaInt = <HTMLInputElement>document.getElementById('txtGPAint');
          const txtGpaDec = <HTMLInputElement>document.getElementById('txtGPAdec');

          if (txtGpaInt != null && txtGpaDec != null) {
            // answ = Number(txtGpaInt.value) + Number(txtGpaDec.value) / 100;
            answ = Number(txtGpaInt.value + '.' + txtGpaDec.value);
          }

          const data = {
            clubProgramAnswerID: Number(qlist.clubProgramAnswerID),
            clubProgramQuestionID: Number(qlist.clubProgramQuestionID),
            userID: Number(this.userID),
            answer: answ,
            questionLabel: qlist.label,
            answerOptionList: qOptionList.length !== 0 ? qOptionList : []
          };
          answers.push(data);
        } else if (qlist.label.toLowerCase() === 'act score') {
          if (!this.actWritten) {
            const q = this.questionForm.controls['actWritten'];
            answ = (q !== null || q.value !== '') ? q.value : '';

            const data = {
              clubProgramAnswerID: Number(qlist.clubProgramAnswerID),
              clubProgramQuestionID: Number(qlist.clubProgramQuestionID),
              userID: Number(this.userID),
              answer: answ,
              questionLabel: 'actWritten',
              answerOptionList: qOptionList.length !== 0 ? qOptionList : []
            };
            answers.push(data);
          }

          if (!this.actVerbal) {
            const q = this.questionForm.controls['actVerbal'];
            answ = (q !== null || q.value !== '') ? q.value : '';

            const data = {
              clubProgramAnswerID: Number(qlist.clubProgramAnswerID),
              clubProgramQuestionID: Number(qlist.clubProgramQuestionID),
              userID: Number(this.userID),
              answer: answ,
              questionLabel: 'actVerbal',
              answerOptionList: qOptionList.length !== 0 ? qOptionList : []
            };
            answers.push(data);
          }

          if (!this.actMath) {
            const q = this.questionForm.controls['actMath'];
            answ = (q !== null || q.value !== '') ? q.value : '';

            const data = {
              clubProgramAnswerID: Number(qlist.clubProgramAnswerID),
              clubProgramQuestionID: Number(qlist.clubProgramQuestionID),
              userID: Number(this.userID),
              answer: answ,
              questionLabel: 'actMath',
              answerOptionList: qOptionList.length !== 0 ? qOptionList : []
            };
            answers.push(data);
          }

          if (!this.actEnglish) {
            const q = this.questionForm.controls['actEnglish'];
            answ = (q !== null || q.value !== '') ? q.value : '';

            const data = {
              clubProgramAnswerID: Number(qlist.clubProgramAnswerID),
              clubProgramQuestionID: Number(qlist.clubProgramQuestionID),
              userID: Number(this.userID),
              answer: answ,
              questionLabel: 'actEnglish',
              answerOptionList: qOptionList.length !== 0 ? qOptionList : []
            };
            answers.push(data);
          }
        } else if (qlist.label.toLowerCase() === 'sat score') {
          if (!this.satReading) {
            const q = this.questionForm.controls['satReading'];
            answ = (q !== null || q.value !== '') ? q.value : '';

            const data = {
              clubProgramAnswerID: Number(qlist.clubProgramAnswerID),
              clubProgramQuestionID: Number(qlist.clubProgramQuestionID),
              userID: Number(this.userID),
              answer: answ,
              questionLabel: 'satReading',
              answerOptionList: qOptionList.length !== 0 ? qOptionList : []
            };
            answers.push(data);
          }

          if (!this.satMath) {
            const q = this.questionForm.controls['satMath'];
            answ = (q !== null || q.value !== '') ? q.value : '';

            const data = {
              clubProgramAnswerID: Number(qlist.clubProgramAnswerID),
              clubProgramQuestionID: Number(qlist.clubProgramQuestionID),
              userID: Number(this.userID),
              answer: answ,
              questionLabel: 'satMath',
              answerOptionList: qOptionList.length !== 0 ? qOptionList : []
            };
            answers.push(data);
          }
        } else {
          const q = this.questionForm.controls[qlist.label];
          answ = (q !== null || q.value !== '') ? q.value : '';

          const data = {
            clubProgramAnswerID: Number(qlist.clubProgramAnswerID),
            clubProgramQuestionID: Number(qlist.clubProgramQuestionID),
            userID: Number(this.userID),
            answer: answ,
            questionLabel: qlist.label,
            answerOptionList: qOptionList.length !== 0 ? qOptionList : []
          };
          answers.push(data);
        }
      }
    }

    return answers;
  }

  onUpload(name, file) {
    this.questionForm.patchValue({ [name]: file });
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/components/questions/all-questions/all-questions.component.ts